import React, { useState } from "react";
import { useAllFundAssets, useDeleteFundAsset } from "@/hooks/useFundAssets";
import { useFunds } from "@/hooks/useFunds";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import { AssetFormDialog } from "@/components/AssetFormDialog";
import { AssetCashflowDialog } from "@/components/AssetCashflowDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { FundAsset } from "@/types";

const MONTHS_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtMonYY(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return `${MONTHS_EN[d.getUTCMonth()]}/${String(d.getUTCFullYear()).slice(2)}`;
}

function fmtMil(v: number | string | null | undefined, dash = true): string {
  const n = Number(v ?? 0);
  if (dash && (v == null || n === 0)) return "—";
  return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
}

function fmtCost(v: number | string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (n === 0) return "—";
  return `(${fmtMil(Math.abs(n))})`;
}

const thStyle: React.CSSProperties = {
  backgroundColor: "rgb(212, 163, 123)",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "11px",
  padding: "6px 8px",
  whiteSpace: "nowrap",
  textAlign: "center",
};
const tdStyle: React.CSSProperties = {
  fontSize: "12px",
  padding: "5px 8px",
  whiteSpace: "nowrap",
  borderBottom: "1px solid hsl(var(--border))",
};
const tdCStyle: React.CSSProperties = { ...tdStyle, textAlign: "center" };
const tdRStyle: React.CSSProperties = { ...tdStyle, textAlign: "center" };
const totStyle: React.CSSProperties = {
  backgroundColor: "rgb(0, 56, 130)",
  color: "#e2eaf5",
  fontWeight: 700,
  fontSize: "12px",
  padding: "6px 8px",
  whiteSpace: "nowrap",
};
const totRStyle: React.CSSProperties = { ...totStyle, textAlign: "center" };

export default function Assets() {
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const canEditAssets = isAdmin || (me?.permissions?.can_edit_assets ?? false);
  const { data: assets } = useAllFundAssets();
  const { data: funds } = useFunds();
  const del = useDeleteFundAsset();

  const [search, setSearch] = useState("");
  const [filterFund, setFilterFund] = useState<string>("all");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState<FundAsset | null>(null);
  const [cashflowAsset, setCashflowAsset] = useState<FundAsset | null>(null);
  const [cashflowOpen, setCashflowOpen] = useState(false);

  const fundName = (id: string) => funds?.find((f) => f.id === id)?.name ?? "—";

  // Busca nas três colunas de texto da tabela: Fund, Asset e Inv. Thesis.
  // O fundo entra pelo NOME (via fundName), não pelo id, porque é o nome que
  // aparece na tela e é como as pessoas pensam — buscar por uuid não serve a
  // ninguém. Mesma abordagem da busca de Investidores em pages/Index.tsx:
  // filtro no cliente, sem ida ao servidor, porque a lista já vem inteira.
  const termo = search.trim().toLowerCase();

  const filtered = (assets ?? [])
    .filter((a) => filterFund === "all" || a.fund_id === filterFund)
    .filter((a) => {
      if (!termo) return true;
      return (
        a.asset_name.toLowerCase().includes(termo) ||
        (a.thesis ?? "").toLowerCase().includes(termo) ||
        fundName(a.fund_id).toLowerCase().includes(termo)
      );
    })
    .sort((a, b) => {
      const da = a.investment_date ? new Date(a.investment_date).getTime() : 0;
      const db = b.investment_date ? new Date(b.investment_date).getTime() : 0;
      return da - db;
    });

  const totalCost     = filtered.reduce((s, a) => s + Number(a.acquisition_cost ?? 0), 0);
  const totalCapRec   = filtered.reduce((s, a) => s + Number(a.capital_received ?? 0), 0);
  const totalFutRec   = filtered.reduce((s, a) => s + Number(a.future_receivables ?? 0), 0);
  const totalFutVal   = filtered.reduce((s, a) => s + Number(a.future_asset_value ?? 0), 0);

  const firstDates = filtered.map(a => a.first_payment_date).filter(Boolean) as string[];
  const lastDates  = filtered.map(a => a.last_payment_date).filter(Boolean) as string[];
  const earliestFirst = firstDates.length ? firstDates.reduce((a, b) => a < b ? a : b) : null;
  const latestLast    = lastDates.length  ? lastDates.reduce((a, b) => a > b ? a : b)  : null;

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await del.mutateAsync(deleteId);
      toast.success("Asset removed");
    } catch {
      toast.error("Error removing asset");
    }
    setDeleteId(null);
  };

  const openEdit = (a: FundAsset, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditAsset(a);
    setFormOpen(true);
  };

  const openAdd = () => {
    setEditAsset(null);
    setFormOpen(true);
  };

  const openCashflows = (a: FundAsset) => {
    setCashflowAsset(a);
    setCashflowOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="IR Jus Capital"
        subtitle="Asset Portfolio"
        actions={
          canEditAssets ? (
            <Button size="sm" onClick={openAdd} className="gap-2">
              <Plus className="h-4 w-4" /> New Asset
            </Button>
          ) : undefined
        }
      />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by asset, thesis or fund..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterFund} onValueChange={setFilterFund}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Funds</SelectItem>
              {(funds ?? []).map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || filterFund !== "all") && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                setFilterFund("all");
              }}
            >
              Clear
            </Button>
          )}
        </div>

        <div className="rounded-lg border border-border overflow-x-auto">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Fund</th>
                <th style={thStyle}>Asset</th>
                <th style={thStyle}>Inv. Thesis</th>
                <th style={thStyle}>Inv. Date</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Acq. Cost</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Cap. Received</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Future Recv.</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Future Asset Val.</th>
                <th style={{ ...thStyle, textAlign: "center" }}>CoC</th>
                <th style={{ ...thStyle, textAlign: "center" }}>IRR</th>
                <th style={thStyle}>1st Pmt</th>
                <th style={{ ...thStyle, textAlign: "center" }}>1st Pmt. Val.</th>
                <th style={thStyle}>Last Pmt</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Last Pmt. Val.</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Tenor</th>
                {canEditAssets && <th style={{ ...thStyle, width: "64px" }} />}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, idx) => (
                <tr
                  key={a.id}
                  style={{
                    backgroundColor: idx % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)",
                    cursor: "pointer",
                  }}
                  onClick={() => openCashflows(a)}
                >
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fundName(a.fund_id)}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{a.asset_name}</td>
                  <td style={tdStyle}>{a.thesis ?? "—"}</td>
                  <td style={tdCStyle}>{fmtMonYY(a.investment_date)}</td>
                  <td style={tdRStyle}>{fmtCost(a.acquisition_cost)}</td>
                  <td style={tdRStyle}>{fmtMil(a.capital_received)}</td>
                  <td style={tdRStyle}>{fmtMil(a.future_receivables)}</td>
                  <td style={tdRStyle}>{fmtMil(a.future_asset_value)}</td>
                  <td style={tdRStyle}>{Number(a.multiple) ? `${fmtMil(a.multiple, false)}x` : "—"}</td>
                  <td style={tdRStyle}>{Number(a.irr) ? `${fmtMil(a.irr, false)}%` : "—"}</td>
                  <td style={tdCStyle}>{fmtMonYY(a.first_payment_date)}</td>
                  <td style={tdRStyle}>{fmtMil(a.first_payment_value)}</td>
                  <td style={tdCStyle}>{fmtMonYY(a.last_payment_date)}</td>
                  <td style={tdRStyle}>{fmtMil(a.last_payment_value)}</td>
                  <td style={tdRStyle}>{Number(a.average_tenor) ? `${fmtMil(a.average_tenor, false)}Y` : "—"}</td>
                  {canEditAssets && (
                    <td
                      style={{ ...tdStyle, padding: "2px 4px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => openEdit(a, e)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(a.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                // Sem esta linha, uma busca sem resultado mostra a tabela só com
                // o cabeçalho — parece tela quebrada em vez de "nada encontrado".
                <tr>
                  <td
                    colSpan={canEditAssets ? 16 : 15}
                    style={{ ...tdCStyle, padding: "24px 8px", color: "hsl(var(--muted-foreground))" }}
                  >
                    {termo || filterFund !== "all"
                      ? "No assets match the current search or filter."
                      : "No assets registered."}
                  </td>
                </tr>
              )}
              {filtered.length > 0 && (
                <tr>
                  <td
                    style={{ ...totStyle, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    colSpan={4}
                  >
                    Total ({filtered.length} assets)
                  </td>
                  <td style={totRStyle}>{fmtCost(totalCost)}</td>
                  <td style={totRStyle}>{fmtMil(totalCapRec)}</td>
                  <td style={totRStyle}>{fmtMil(totalFutRec)}</td>
                  <td style={totRStyle}>{fmtMil(totalFutVal)}</td>
                  <td style={{ ...totStyle, textAlign: "center" }} />
                  <td style={{ ...totStyle, textAlign: "center" }} />
                  <td style={{ ...totStyle, textAlign: "center" }}>{fmtMonYY(earliestFirst)}</td>
                  <td style={{ ...totStyle, textAlign: "center" }} />
                  <td style={{ ...totStyle, textAlign: "center" }}>{fmtMonYY(latestLast)}</td>
                  <td style={{ ...totStyle, textAlign: "center" }} />
                  <td style={{ ...totStyle, textAlign: "center" }} />
                  {canEditAssets && <td style={totStyle} />}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AssetFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        asset={editAsset}
        funds={funds ?? []}
      />

      <AssetCashflowDialog
        asset={cashflowAsset}
        open={cashflowOpen}
        onOpenChange={setCashflowOpen}
        readOnly={!canEditAssets}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The asset and its cashflows will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
